import { useState } from "react";
import { useNavigate } from "react-router-dom";
import Layout from "@/components/Layout";
import { Button, Card, Input, Select, Spinner, fmtKg } from "@/components/ui/Primitives";
import { api, formatErr } from "@/lib/api";
import { ArrowLeft, Save, Tractor, MessageSquare, Users } from "lucide-react";

export default function TerceirosNFNew() {
  const nav = useNavigate();
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");
  const [form, setForm] = useState({
    nota_numero: "",
    fornecedor: "",             // motorista/transportador — mantido pra compatibilidade
    cliente_dono: "",           // Dono da mercadoria (obrigatório)
    cliente_dono_cnpj: "",
    tipo_cebola: "amarela",
    unidade: "Matriz VGS",
    peso_bruto: "",
    peso_tara: "",
    valor_nf: "",
    data_entrada: new Date().toISOString().slice(0, 10),
    motorista: "",
    placa: "",
    origem: "",
    preco_servico_sc: 3,
    preco_sacaria_sc: 2,
    usa_sacaria_propria: false,
    observacoes: "",
  });

  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });
  const setBool = (k) => (e) => setForm({ ...form, [k]: e.target.checked });
  const pesoLiquido = (Number(form.peso_bruto) || 0) - (Number(form.peso_tara) || 0);

  const submit = async (goToBenef) => {
    setBusy(true); setErr("");
    try {
      const payload = {
        ...form,
        fornecedor: form.cliente_dono, // usa cliente_dono como identificação principal
        peso_bruto: Number(form.peso_bruto) || 0,
        peso_tara: Number(form.peso_tara) || 0,
        peso_liquido: pesoLiquido,
        valor_nf: Number(form.valor_nf) || 0,
        preco_servico_sc: Number(form.preco_servico_sc) || 0,
      };
      const { data } = await api.post("/terceiros/nfs", payload);
      if (goToBenef) {
        await api.post(`/invoices/${data.id}/iniciar-beneficiamento`);
        nav(`/invoices/${data.id}/beneficiamento`);
      } else {
        nav(`/terceiros/nfs`);
      }
    } catch (e2) { setErr(formatErr(e2)); setBusy(false); }
  };

  return (
    <Layout
      title="Nova NF de Terceiros"
      subtitle="Registro de mercadoria recebida para beneficiamento por conta de terceiros"
      actions={<Button variant="secondary" onClick={() => nav("/terceiros/nfs")}><ArrowLeft size={16} /> Voltar</Button>}
    >
      <div className="mb-4 p-3 rounded-lg bg-purple-50 border border-purple-300 text-sm text-purple-900 flex items-start gap-2">
        <Users size={16} className="mt-0.5 flex-shrink-0" />
        <div>
          <strong>Beneficiamento a Terceiros</strong> — esta mercadoria é do cliente e será apenas processada aqui.
          <span className="block text-xs mt-0.5">Nenhuma Conta a Pagar é gerada; ao final do beneficiamento, será cobrado o valor de serviço configurado abaixo.</span>
        </div>
      </div>

      <form onSubmit={(e) => { e.preventDefault(); submit(false); }} className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        <div className="lg:col-span-2 space-y-6">
          <Card className="p-6">
            <h3 className="font-heading text-lg font-bold text-ink-900 mb-4">Cliente & NF</h3>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <Input label="Nome do dono da mercadoria" required value={form.cliente_dono} onChange={set("cliente_dono")} data-testid="tn-cliente-dono" />
              <Input label="CNPJ do cliente (opcional)" value={form.cliente_dono_cnpj} onChange={set("cliente_dono_cnpj")} data-testid="tn-cliente-cnpj" />
              <Input label="Número da NF (emitida pelo cliente)" required value={form.nota_numero} onChange={set("nota_numero")} data-testid="tn-numero" />
              <Input label="Data de entrada" type="date" required value={form.data_entrada} onChange={set("data_entrada")} data-testid="tn-data" />
              <Select label="Tipo de cebola" value={form.tipo_cebola} onChange={set("tipo_cebola")} data-testid="tn-tipo">
                <option value="amarela">Amarela</option>
                <option value="roxa">Roxa</option>
                <option value="branca">Branca</option>
                <option value="pelada">Pelada</option>
              </Select>
              <Select label="Unidade de beneficiamento" required value={form.unidade} onChange={set("unidade")} data-testid="tn-unidade">
                <option value="Matriz VGS">Matriz VGS</option>
                <option value="Araxá">Araxá</option>
                <option value="Ituporanga">Ituporanga</option>
              </Select>
              <Input label="Origem (cidade/UF)" value={form.origem} onChange={set("origem")} data-testid="tn-origem" />
              <Input label="Valor da NF (referência)" type="number" step="0.01" value={form.valor_nf} onChange={set("valor_nf")} data-testid="tn-valor" />
            </div>
          </Card>

          <Card className="p-6">
            <h3 className="font-heading text-lg font-bold text-ink-900 mb-4">Pesagem</h3>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <Input label="Peso bruto (kg)" type="number" step="0.01" required value={form.peso_bruto} onChange={set("peso_bruto")} data-testid="tn-peso-bruto" />
              <Input label="Tara (kg)" type="number" step="0.01" value={form.peso_tara} onChange={set("peso_tara")} data-testid="tn-peso-tara" />
              <div>
                <div className="block text-xs font-bold uppercase tracking-[0.1em] text-ink-500 mb-1.5">Peso líquido</div>
                <div className="font-mono text-2xl font-semibold text-purple-700 px-3 py-2 bg-purple-50 rounded-md border border-purple-200" data-testid="tn-peso-liquido">
                  {fmtKg(pesoLiquido)}
                </div>
              </div>
            </div>
          </Card>

          <Card className="p-6">
            <h3 className="font-heading text-lg font-bold text-ink-900 mb-4">Serviço de Beneficiamento</h3>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <Input label="Preço do serviço (R$/saco beneficiado)" type="number" step="0.01" value={form.preco_servico_sc} onChange={set("preco_servico_sc")} data-testid="tn-preco-servico" />
              <label className="flex items-center gap-2 p-3 rounded-md bg-purple-50 border border-purple-200 cursor-pointer" data-testid="tn-usa-sacaria-label">
                <input
                  type="checkbox"
                  checked={form.usa_sacaria_propria}
                  onChange={setBool("usa_sacaria_propria")}
                  className="w-4 h-4"
                  data-testid="tn-usa-sacaria"
                />
                <div className="text-sm">
                  <div className="font-semibold text-ink-900">Usar sacaria da empresa</div>
                  <div className="text-xs text-ink-600">Desconta do nosso estoque e cobra à parte</div>
                </div>
              </label>
              {form.usa_sacaria_propria && (
                <Input label="Preço da sacaria (R$/saco)" type="number" step="0.01" value={form.preco_sacaria_sc} onChange={set("preco_sacaria_sc")} data-testid="tn-preco-sacaria" />
              )}
            </div>
          </Card>

          <Card className="p-6">
            <h3 className="font-heading text-lg font-bold text-ink-900 mb-4">Transporte</h3>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <Input label="Motorista" value={form.motorista} onChange={set("motorista")} data-testid="tn-motorista" />
              <Input label="Placa" value={form.placa} onChange={set("placa")} data-testid="tn-placa" />
            </div>
          </Card>

          <Card className="p-6">
            <h3 className="font-heading text-lg font-bold text-ink-900 mb-4 flex items-center gap-2"><MessageSquare size={18} /> Observações</h3>
            <textarea
              value={form.observacoes} onChange={set("observacoes")} rows={3}
              placeholder="Anotações livres sobre a entrega..."
              className="w-full rounded-md bg-stone-50 border border-stone-300 px-3 py-2 focus:outline-none focus:ring-2 focus:ring-brand-500 text-sm"
              data-testid="tn-observacoes"
            />
          </Card>
        </div>

        <div className="space-y-4">
          <Card className="p-6 sticky top-20">
            <h3 className="font-heading font-bold text-ink-900 mb-2">Resumo</h3>
            <p className="text-sm text-ink-500 mb-4">Escolha se quer apenas salvar ou já iniciar o beneficiamento.</p>
            <div className="space-y-2 text-sm">
              <div className="flex justify-between"><span className="text-ink-500">Cliente</span><span className="truncate font-semibold">{form.cliente_dono || "—"}</span></div>
              <div className="flex justify-between"><span className="text-ink-500">NF</span><span className="font-mono">{form.nota_numero || "—"}</span></div>
              <div className="flex justify-between"><span className="text-ink-500">Líquido</span><span className="font-mono">{fmtKg(pesoLiquido)}</span></div>
              <div className="flex justify-between"><span className="text-ink-500">Serviço</span><span className="font-mono">R$ {Number(form.preco_servico_sc || 0).toFixed(2)}/sc</span></div>
              {form.usa_sacaria_propria && (
                <div className="text-xs text-purple-700 font-semibold pt-1">✓ Sacaria da empresa (a cobrar)</div>
              )}
            </div>
            {err && <div className="text-sm text-danger mt-4 bg-red-50 border border-red-200 px-3 py-2 rounded-md">{err}</div>}
            <div className="space-y-2 mt-5">
              <Button type="submit" size="lg" className="w-full !bg-purple-600 hover:!bg-purple-700" disabled={busy} data-testid="tn-submit">
                {busy ? <Spinner size={16} /> : <><Save size={16} /> Apenas Salvar</>}
              </Button>
              <Button type="button" variant="secondary" size="lg" className="w-full" disabled={busy} onClick={() => submit(true)} data-testid="tn-submit-benef">
                {busy ? <Spinner size={16} /> : <><Tractor size={16} /> Salvar + Iniciar Beneficiamento</>}
              </Button>
            </div>
          </Card>
        </div>
      </form>
    </Layout>
  );
}
